import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  TextField,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useMemo } from 'react';

/** One selectable row: the stable filter value plus its display label. */
export interface FilterOption<V extends string | number> {
  value: V;
  label: string;
}

interface FilterMultiSelectProps<V extends string | number> {
  label: string;
  /** Available options (already cross-filtered by the parent). */
  options: FilterOption<V>[];
  /** Currently selected values. */
  value: V[];
  onChange: (values: V[]) => void;
  loading?: boolean;
  disabled?: boolean;
  /** Shrinks the control for the compact filter row. */
  size?: 'small' | 'medium';
  minWidth?: number;
}

/**
 * Generic multi-select dropdown used by the Visibility filter row. The
 * parent owns the option set (so it can be narrowed by the other filters);
 * this component only renders + reports selection. Currently-selected values
 * that fall outside `options` are still rendered as removable chips so a
 * selection never silently disappears when another filter narrows it out.
 */
export function FilterMultiSelect<V extends string | number>({
  label,
  options,
  value,
  onChange,
  loading,
  disabled,
  size = 'small',
  minWidth = 200,
}: FilterMultiSelectProps<V>) {
  // Merge any selected value missing from `options` (narrowed out by a
  // sibling filter) so its chip still renders and can be removed.
  const mergedOptions = useMemo<FilterOption<V>[]>(() => {
    const byValue = new Map<V, FilterOption<V>>();
    for (const o of options) byValue.set(o.value, o);
    for (const v of value) {
      if (!byValue.has(v)) byValue.set(v, { value: v, label: String(v) });
    }
    return Array.from(byValue.values());
  }, [options, value]);

  const selectedOptions = useMemo<FilterOption<V>[]>(
    () => mergedOptions.filter((o) => value.includes(o.value)),
    [mergedOptions, value],
  );

  return (
    <Autocomplete<FilterOption<V>, true, false, false>
      multiple
      disableCloseOnSelect
      size={size}
      disabled={disabled}
      options={mergedOptions}
      value={selectedOptions}
      onChange={(_, next) => onChange(next.map((o) => o.value))}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      loading={loading}
      sx={{ minWidth, flex: 1 }}
      renderTags={(rows, getTagProps) =>
        rows.map((row, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} {...tagProps} size="small" label={row.label} />;
        })
      }
      renderOption={(props, option, { selected }) => (
        <Box component="li" {...props} key={option.value}>
          <Checkbox
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            style={{ marginRight: 8 }}
            checked={selected}
          />
          {option.label}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
